export class AsyncLock {
  constructor(defaultTimeout = 30000) {
    this.queue = Promise.resolve()
    this.defaultTimeout = defaultTimeout
    this.activeReleaseResolver = null // Stores the resolver for the currently active lock
  }

  /**
   * Acquires the lock.
   * - Awaits until the previous lock is released.
   * - If timeout is reached, releases its own spot and returns null.
   * @param {number} [timeout] - Custom timeout in ms.
   * @returns {Promise<Function|null>} - Returns release callback or null.
   */
  async acquire(timeout = this.defaultTimeout) {
    let unlockNext

    // 1. Create the promise for this turn
    const myTurnIsOver = new Promise((resolve) => {
      unlockNext = resolve
    })

    // 2. Join the queue
    const previousTurn = this.queue
    this.queue = previousTurn.then(() => myTurnIsOver).catch(() => myTurnIsOver)

    // 3. Define the standard release callback
    let released = false
    const releaseCallback = () => {
      if (!released) {
        released = true
        // If this was the active lock being tracked, clear the reference
        if (this.activeReleaseResolver === unlockNext) {
          this.activeReleaseResolver = null
        }
        unlockNext()
      }
    }

    // 4. Wait for turn
    let timer
    const timeoutPromise = new Promise((resolve) => {
      timer = setTimeout(() => resolve('TIMEOUT'), timeout)
    })

    try {
      const result = await Promise.race([previousTurn.then(() => 'ACQUIRED'), timeoutPromise])

      clearTimeout(timer)

      if (result === 'TIMEOUT') {
        releaseCallback() // Step out of line
        return null
      }

      // We have the lock now. Track this resolver as the active one.
      this.activeReleaseResolver = unlockNext

      return releaseCallback
    } catch (err) {
      clearTimeout(timer)
      releaseCallback()
      return null
    }
  }

  /**
   * Manually releases the currently active lock.
   * Useful for cleanup or emergency resets.
   */
  release() {
    if (this.activeReleaseResolver) {
      // Resolve the promise holding the current lock
      this.activeReleaseResolver()
      this.activeReleaseResolver = null
      return true // Indicates a lock was released
    }
    return false // No active lock to release
  }
}
